import { useContext, useEffect, useRef, useState } from "react";
import Grid from "@mui/material/Grid2";
import { useTranslation } from "react-i18next";
import RoomsRealtime from "@common/components/ListDetailViews/Layout";
import {
  IDetailContext,
  RoomsRealtimeContext,
} from "@common/components/ListDetailViews/Layout/context";
import DetailView from "./DetailView";
import { useNavigate, useParams } from "react-router-dom";
import { useErrorNotification } from "@common/components/Notification";
import {
  ViewTabContext,
  WithTabsProvider,
} from "@common/components/ListDetailViews/Layout/NavigationBar/ViewsTabs/context";
import { usePolling } from "@common/hooks";
import { canEditRoom, canViewTicket } from "@common/utils/user";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import { MeetingInstance } from "@services/meetings/types";
import { getMonitoringMeetingId } from "@services/monitoring";
import { getMeetingById } from "@services/meetings";

import { getMeetingTabs } from "./utils";
import Loader from "@common/components/Suspense/Loader";

const MeetingDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [openNavigation, setOpenNavigation] = useState(false);
  const [openAccordion, setOpenAccordion] = useState<string>("");
  const detailFrameRef = useRef<HTMLDivElement>(null);
  const dispatchError = useErrorNotification();

  const { meetingid } = useParams();
  const [user] = useAuthenticatedUser();
  const [meeting, setMeeting] = useState<MeetingInstance | undefined>();
  const {
    isTabsUpdatedWithPermissions,
    setSelectedTab,
    dispatch: dispatchTabs,
  } = useContext(ViewTabContext);
  const loading = meeting === undefined;

  const refetch = async () => {
    try {
      if (meetingid?.includes("|")) {
        const splitId = meetingid?.split("|");
        let meeting = await getMonitoringMeetingId(
          Number(splitId[0]),
          Number(splitId[1])
        );
        setMeeting(meeting);
      } else {
        let meeting = await getMeetingById(meetingid as string);
        setMeeting(meeting);
      }
    } catch (e) {
      navigate("/");
      dispatchError(e, { resourceName: t("Meeting") });
    }
  };

  const poll = usePolling(refetch, 30e3);

  useEffect(() => {
    poll.start();
    return () => poll.stop();
  }, []);

  useEffect(() => {
    if (meeting === undefined) refetch();
  }, [meeting]);

  useEffect(() => {
    if (!isTabsUpdatedWithPermissions && user && meeting) {
      const tabs = getMeetingTabs(user, meeting, meetingid);

      dispatchTabs({
        tabs,
        isTabsUpdatedWithPermissions: true,
      });
      setSelectedTab(tabs[0].name);
    }
  }, [meeting]);

  const defaultContext: IDetailContext = {
    loading,
    meeting,
    refetch: refetch,
    isPaused: false,
    detailFrameRef,
    openAccordion,
    setOpenAccordion,
    resetDevice: () => {},
  };

  if (!isTabsUpdatedWithPermissions) return <Loader />;

  return (
    <Grid
      container
      direction="column"
      sx={{
        justifyContent: "center",
        height: "100%",
        width: !!openAccordion ? `calc(100% + 500px)` : "100%",
        transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
        transitionDuration: ".2s",
        transitionProperty: "width",
      }}
    >
      <RoomsRealtimeContext.Provider
        value={{
          openNavigation,
          setOpenNavigation,
          detail: defaultContext,
          canEdit: canEditRoom(user),
        }}
      >
        <RoomsRealtime>
          <DetailView />
        </RoomsRealtime>
      </RoomsRealtimeContext.Provider>
    </Grid>
  );
};

export default WithTabsProvider(MeetingDetail);
